import buildLegacyEnvConfig from "./legacyEnvConfig";
import { validateAppConfig } from "./validateAppConfig";
import type { ConfigSchema } from "./validateAppConfig";

export interface BaseAppConfig {
  appName: string;
  storageNamespace: string;
  storageEncryptionKey: string;
  apiBaseUrl: string;
  visaHeaderName: string;
  visaHeaderValue: string;
  viewAsEnabled: boolean;
  viewAsKey: string | null;
  viewAsUserProperty: string | null;
  routerBasename: string;
  publicUrl: string;
  templatesPath: string;
  tinymceApiKey: string | null;
  primaryColor: string;
  secondaryColor: string;
  companyNameEn: string;
  companyNameAr: string;
  companyLogoUrl: string;
  companyLogoMenuUrl: string | null;
  companyLogoMiniUrl: string | null;
  companyLogoPadding: number;
}

declare global {
  interface Window {
    __APP_CONFIG__?: unknown;
  }
}

export type AppConfigSource = "runtime" | "legacy-env";

let current: BaseAppConfig | null = null;
let source: AppConfigSource | null = null;
let initialised = false;
let legacyReadBeforeInit = false;

const READ_BEFORE_INIT_ERROR =
  "Configuration was read before initAppConfig() ran, so build-time env values were used. Move that read inside a function or component.";

/**
 * Call once, before anything reads config. Projects that never call it stay on
 * their build-time env (legacy mode). The two sources are never mixed.
 *
 * `requireRuntime: false` still validates a PRESENT `window.__APP_CONFIG__` and
 * fails on it when invalid; the option only decides whether an ABSENT one is an
 * error.
 */
export const initAppConfig = (
  schema: ConfigSchema,
  options: { requireRuntime: boolean }
): { errors: string[] } => {
  initialised = true;
  current = null;
  source = null;
  const readBeforeInit = options.requireRuntime && legacyReadBeforeInit;
  const raw = window.__APP_CONFIG__;
  if (raw === undefined) {
    if (options.requireRuntime) {
      const errors = [
        "app-config.js was not loaded, so window.__APP_CONFIG__ is undefined.",
      ];
      if (readBeforeInit) errors.push(READ_BEFORE_INIT_ERROR);
      return { errors };
    }
    current = buildLegacyEnvConfig();
    source = "legacy-env";
    return { errors: [] };
  }
  const errors = validateAppConfig(raw, schema);
  if (readBeforeInit) errors.push(READ_BEFORE_INIT_ERROR);
  if (errors.length === 0) {
    current = Object.freeze({ ...(raw as BaseAppConfig) });
    source = "runtime";
  }
  return { errors };
};

export const getAppConfig = <T extends BaseAppConfig = BaseAppConfig>(): T => {
  if (current === null && !initialised) {
    // Legacy config is snapshotted on first read. That is fine for CRA builds,
    // where env values are inlined at build time; tests that mutate
    // process.env must call __resetAppConfigForTests() first.
    legacyReadBeforeInit = true;
    current = buildLegacyEnvConfig();
    source = "legacy-env";
  }
  if (current === null)
    throw new Error(
      "App configuration is not available: initAppConfig() reported errors."
    );
  return current as T;
};

export const getAppConfigSource = () => source;

/** Test-only. Jest and Vitest both run these files, so resetting must not use a runner API. */
export const __resetAppConfigForTests = () => {
  current = null;
  source = null;
  initialised = false;
  legacyReadBeforeInit = false;
};
